import { useQuery } from "@tanstack/react-query";
import { get } from "lodash";
import { useEffect, useState } from "react";
import { ProcessReportStatus } from "@/app/[lng]/reports/_lib/constants";
import useCurrentUserManager from "@/hooks/use-current-user-manager";
import { useOneTimePurchaseExclusion } from "@/hooks/use-one-time-purchase-exclusion";
import type { ApiResponse } from "@/repositories/common-types";
import ReportsRepository from "@/repositories/reports/reports-repository";
import type { ProcessReport, ProcessReportResponse } from "@/repositories/reports/types";
import useBoundStore from "@/store/bound-store";
import usePersistedBoundStore from "@/store/persisted-bound-store";
import { isAuthenticated } from "@/utils/auth";

const reportsRepository = new ReportsRepository();

function getVisibleAvailableReports(
  data: ApiResponse<ProcessReportResponse> | undefined,
  isOneTimePurchaseExcluded: boolean
): ProcessReport[] {
  if (isOneTimePurchaseExcluded) {
    return [];
  }

  return get(data, "data.processes", []);
}

export default function useReports() {
  const [isAuthenticatedUser, setIsAuthenticatedUser] = useState(false);
  const currentUserManager = useCurrentUserManager();
  const personData = useBoundStore.use.person();
  const environment = usePersistedBoundStore.use.environment();

  const isControlledProcess = get(environment, "environmentType.Name") === "CONTROLLED";
  const isProfileComplete = !!get(personData, "completeProfile", false);
  const isControlledBasic = isControlledProcess && !isProfileComplete;
  const { isOneTimePurchaseExcluded } = useOneTimePurchaseExclusion();

  const {
    data: dataAcquired,
    isLoading,
    error,
    refetch: refetchAcquired,
  } = useQuery({
    queryKey: ["reports-adquired", ProcessReportStatus.BUYED],
    queryFn: () => reportsRepository.getReports(ProcessReportStatus.BUYED),
    enabled: isAuthenticatedUser && !isControlledBasic,
    refetchOnMount: true,
  });
  const {
    data: dataAvailable,
    isLoading: isLoadingAvailable,
    error: errorAvailable,
    refetch: refetchAvailable,
  } = useQuery({
    queryKey: ["reports-available", ProcessReportStatus.AVAILABLE],
    queryFn: () => reportsRepository.getReports(ProcessReportStatus.AVAILABLE),
    enabled: isAuthenticatedUser && !isControlledBasic && !isOneTimePurchaseExcluded,
    refetchOnMount: true,
  });

  const reportsAcquired = get(dataAcquired, "data.processes", []);
  const reportsAcquiredCount = reportsAcquired.length;
  const hasAcquiredReports = reportsAcquiredCount > 0;

  const reportsAvailable = getVisibleAvailableReports(dataAvailable, isOneTimePurchaseExcluded);
  const reportsAvailableCount = reportsAvailable.length;
  const hasAvailableReports = reportsAvailableCount > 0;

  useEffect(() => {
    const checkAuth = async () => {
      const authStatus = await isAuthenticated(currentUserManager);
      setIsAuthenticatedUser(authStatus);
    };
    checkAuth();
  }, [currentUserManager]);
  return {
    reportsAcquired,
    reportsAcquiredCount,
    hasAcquiredReports,
    isLoading,
    error,
    refetchAcquired,
    reportsAvailable,
    reportsAvailableCount,
    hasAvailableReports,
    isLoadingAvailable,
    errorAvailable,
    refetchAvailable,
  };
}
